import { Button, message } from 'antd';
import AuthContext from 'components/app/Auth/AuthContext';
import FeatureFlag, { Feature } from 'components/app/FeatureFlag';
import JSONTree from 'components/ui/JSONTree';
import VerticalTable from 'components/ui/Table/VerticalTable';
import QueryResult from 'components/util/QueryResult';
import gql from 'graphql-tag';
import USER_BY_ID_QUERY from 'queries/userById';
import React, { useContext } from 'react';
import { Mutation, Query } from 'react-apollo';
import styled from 'styled-components';
import { ActionResult, ActiveTask, AutomationFunction, CloudAccount, User } from 'typings';
import { getProvider } from 'utilities/cloudAccount';

const Root = styled.div`
  border: 1px solid ${(p) => p.theme.grey300};
  margin-bottom: 20px;

  > * {
    border-bottom: 1px solid ${(p) => p.theme.grey300};
  }

  .vt-label-column {
    width: 175px;
    padding-right: 16px;
    text-align: right;
  }

  .table-wrap {
    padding: 12px 0;
  }

  .json-tree-wrap {
    background-color: rgb(238, 238, 238);
    padding: 4px 16px;
    max-height: 400px;
    overflow-y: auto;
  }
`;

const ACCOUNT = gql`
  query AccountForGovernanceActivity($id: ID!) {
    account(id: $id) {
      account_id
      name
      nickname
      provider
    }
  }
`;

const AUTOMATION_FUNCTION = gql`
  query AutomationFunctionsForActivityDetail($id: [String]!) {
    automationFunctions(id: $id) {
      nodes {
        id
        key
        name
        description
        effects
        parameters {
          name
          key
          type
        }
        revertFunction {
          functionId
        }
      }
    }
  }
`;

const REVERT_ACTION = gql`
  mutation revertAction($id: ID!) {
    revertAction(id: $id) {
      id
      clientId
      taskId
      subjectId
      status
      createdAt
      updatedAt
    }
  }
`;

interface Props {
  actionResult: ActionResult;
}

function ActivityDetail(props: Props) {
  const { actionResult } = props;

  const authContext = useContext(AuthContext);

  const { automationFunctionId, detail, createdBy, createdByUsername, issue } = actionResult;
  const { detailResponse, error, itemKey, sourceEvent } = detail || {};
  const { context } = sourceEvent || {};
  let { region, vendor, accountId, awsAccountId } = context || {};

  if (issue && !sourceEvent) {
    awsAccountId = issue.awsAccountId;
    accountId = issue.accountId;
    region = issue.region;
  }

  let provider = getProvider(vendor);

  let rows: any[] = [];
  if (detailResponse) {
    rows = rows.concat([
      {
        key: 'detailResponse',
        label: 'Response',
        dataIndex: 'detailResponse',
        render: () => {
          return detailResponse;
        }
      }
    ]);
  }

  if (error) {
    rows = rows.concat([
      {
        key: 'error',
        label: 'Error',
        dataIndex: 'error',
        render: () => {
          return JSON.stringify(error);
        }
      }
    ]);
  }

  if (provider || awsAccountId) {
    rows = rows.concat([
      {
        key: 'awsAccountId',
        label: 'Account',
        dataIndex: 'awsAccountId',
        render: () => {
          return (
            <Query query={ACCOUNT} variables={{ id: accountId }} fetchPolicy={'cache-first'}>
              {({ data, loading, error }) => (
                <QueryResult
                  data={data}
                  loading={loading}
                  error={error}
                  loadingHeight="8"
                  loadingCenterVertically={false}
                  size="small"
                >
                  {() => {
                    if (error || !data) {
                      return <>{`${provider} ${awsAccountId}`}</>;
                    }
                    const account: CloudAccount = data.account;
                    if (!account) {
                      return <>{`${provider} ${awsAccountId}`}</>;
                    }
                    const { nickname } = account;
                    provider = account.provider;
                    return <>{`${provider} ${awsAccountId} ${nickname}`}</>;
                  }}
                </QueryResult>
              )}
            </Query>
          );
        }
      }
    ]);
  }

  if (createdByUsername) {
    rows = rows.concat([
      {
        key: 'user',
        label: 'User',
        dataIndex: 'createdByUsername'
      }
    ]);
  } else if (createdBy) {
    rows = rows.concat([
      {
        key: 'user',
        label: 'User',
        dataIndex: 'createdBy',
        render: () => {
          return (
            <Query query={USER_BY_ID_QUERY} variables={{ id: createdBy }} fetchPolicy={'cache-first'}>
              {({ data, loading, error }) => (
                <QueryResult
                  data={data}
                  loading={loading}
                  error={error}
                  loadingHeight="8"
                  loadingCenterVertically={false}
                  size="small"
                >
                  {() => {
                    if (error || !data) {
                      return <>{createdBy}</>;
                    }
                    const user: User = data.userById;
                    if (!user) {
                      return <>{createdBy}</>;
                    }
                    actionResult.createdByUsername = user.username;
                    return <>{user.username}</>;
                  }}
                </QueryResult>
              )}
            </Query>
          );
        }
      }
    ]);
  }

  if (region) {
    rows = rows.concat([
      {
        key: 'region',
        label: 'Region',
        dataIndex: 'region',
        render: () => {
          return region;
        }
      }
    ]);
  }

  if (itemKey) {
    rows = rows.concat([
      {
        key: 'itemKey',
        label: 'Item',
        dataIndex: 'itemKey',
        render: () => {
          return itemKey;
        }
      }
    ]);
  }

  const jsonTreeRow = {
    key: 'detail',
    label: 'Detail',
    dataIndex: 'detail',
    render: () => {
      return (
        <div className="json-tree-wrap">
          <JSONTree data={actionResult.detail} depth={1} />
        </div>
      );
    }
  };

  if (!automationFunctionId) {
    rows = rows.concat([jsonTreeRow]);

    return (
      <Root>
        <div className="table-wrap">
          <VerticalTable dataSource={actionResult} rows={rows} classes={{ labelCol: 'vt-label-column' }} />
        </div>
      </Root>
    );
  }

  return (
    <Root>
      <FeatureFlag features={[Feature.ACTION_RESULT_UNDO]}>
        {(flags) => (
          <Query query={AUTOMATION_FUNCTION} variables={{ id: [automationFunctionId] }} fetchPolicy={'cache-first'}>
            {({ loading, data, error }) => (
              <QueryResult
                loading={loading}
                data={data}
                error={error}
                loadingHeight="8"
                loadingCenterVertically={false}
                size="small"
              >
                {() => {
                  if (!data || error) return null;
                  const func: AutomationFunction = data.automationFunctions.nodes[0];

                  if (!func || !func.revertFunction || !func.revertFunction.functionId) {
                    rows = rows.concat([jsonTreeRow]);

                    return (
                      <div className="table-wrap">
                        <VerticalTable
                          dataSource={actionResult}
                          rows={rows}
                          classes={{ labelCol: 'vt-label-column' }}
                        />
                      </div>
                    );
                  }

                  return (
                    <Query
                      query={AUTOMATION_FUNCTION}
                      variables={{ id: [func.revertFunction.functionId] }}
                      fetchPolicy={'cache-first'}
                    >
                      {({ loading, data, error }) => (
                        <QueryResult
                          loading={loading}
                          data={data}
                          error={error}
                          loadingHeight="8"
                          loadingCenterVertically={false}
                          size="small"
                        >
                          {() => {
                            if (!data || error) return null;
                            const revertFunction: AutomationFunction = data.automationFunctions.nodes[0];

                            if (!revertFunction) return null;

                            const { name: revertFunctionName } = revertFunction;

                            if (flags[Feature.ACTION_RESULT_UNDO]) {
                              rows = rows.concat([
                                {
                                  key: 'undo',
                                  label: 'Undo',
                                  dataIndex: 'undo',
                                  render: (val) => {
                                    return (
                                      <Mutation mutation={REVERT_ACTION}>
                                        {(revertAction, { loading }) => {
                                          return (
                                            <Button
                                              size="small"
                                              disabled={loading}
                                              loading={loading}
                                              onClick={async () => {
                                                const variables = {
                                                  id: actionResult.id
                                                };
                                                const response: any = await revertAction({ variables });
                                                if (response.errors) {
                                                  const errorMessages = response.errors.map((error) => error.message);
                                                  const messages = errorMessages.join('<br />');
                                                  message.error(`Undo action failed with error: ${messages}`);
                                                  return;
                                                } else {
                                                  const dfunction: ActiveTask = response.data.revertAction;
                                                  if (authContext?.isDisruptOpsUser) {
                                                    message.success(
                                                      <>
                                                        Action started successfully.{' '}
                                                        <a
                                                          target="_blank"
                                                          rel="noopener noreferrer"
                                                          href={`/dev/tasks/${dfunction.taskId}`}
                                                        >
                                                          Task: {dfunction.taskId}, Status: {dfunction.status}
                                                        </a>
                                                      </>
                                                    );
                                                  } else {
                                                    message.success(
                                                      `Detector started successfully. (Task: ${dfunction.taskId}, Status: ${dfunction.status})`
                                                    );
                                                  }
                                                }
                                              }}
                                            >
                                              {revertFunctionName}
                                            </Button>
                                          );
                                        }}
                                      </Mutation>
                                    );
                                  }
                                },
                                jsonTreeRow
                              ]);
                            }

                            return (
                              <div className="table-wrap">
                                <VerticalTable
                                  dataSource={actionResult}
                                  rows={rows}
                                  classes={{ labelCol: 'vt-label-column' }}
                                />
                              </div>
                            );
                          }}
                        </QueryResult>
                      )}
                    </Query>
                  );
                }}
              </QueryResult>
            )}
          </Query>
        )}
      </FeatureFlag>
    </Root>
  );
}

export default ActivityDetail;
